import React from "react";
import { Container, Card, Form, Button, Spinner, Alert, Modal } from "react-bootstrap";
import { FiCheckCircle, FiX, FiSend, FiDownload } from "react-icons/fi";
import type { Row as TanRow } from "@tanstack/react-table";
import type { ReportColumnsSpec, ReportMetricRow } from "@core/components/ReportTreeTable";
import ReportTreeTable from "@core/components/ReportTreeTable";
import ReportActionsPanel from "@core/components/ReportActionsPanel";
import PageBreadcrumb from "@core/components/PageBreadcrumb";
import { useEntityBasePath } from "@core/hooks/useEntityBasePath";
import { departmentReportsListToViewTrail } from "@core/navigation/pageBreadcrumbTrails";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import DepartmentReportMediaPanel from "@core/reports/components/DepartmentReportMediaPanel";
import RequestReviewModal from "@core/reports/components/RequestReviewModal";
import ReportPreviewModal, { GenericSubmissionPreview } from "@core/reports/components/ReportPreviewModal";
import ReportSectionTables from "@core/reports/components/ReportSectionTables";
import ReportSectionViewToolbar from "@core/reports/components/ReportSectionViewToolbar";
import TemplateRowPreviewButton from "@core/reports/components/TemplateRowPreviewButton";
import TemplateRowReviewAction from "@core/reports/components/TemplateRowReviewAction";
import SubmissionSummaryControl from "@core/reports/components/SubmissionSummaryControl";
import DeadlineStatusBanner from "@core/reports/components/DeadlineStatusBanner";
import {
  isDepartmentReportDeadlineComplete,
  shouldShowDeadlineUrgencyBanner,
} from "@features/report/tag/utils/reportTagDeadline";
import {
  readSectionLens,
  type SectionLensKey,
} from "@core/reports/utils/reportSectionViewLens";
import {
  canApproveDepartmentReport,
  canDownloadReport,
  canRequestDepartmentReportReview,
  canSubmitDepartmentReport,
  canValidateDepartmentReport,
  getRequestReviewCopy,
  formatTimelineEventSubtitle,
  getWorkflowStatusLabel,
  normalizeReportWorkflowStatus,
} from "@core/reports/utils/reportWorkflow";
import NoDepartmentReportNotice from "@core/reports/components/NoDepartmentReportNotice";
import type { UseReportPageResult } from "@core/reports/hooks/useReportPage";
import type { OfficeReportTemplateRow, ReportFormSectionViewMeta, ReportFormTimelineEvent } from "@core/reports/api/types";
import {
  requestDistrictActivityEntryReviewAsync,
  requestNationActivityEntryReviewAsync,
} from "@features/report/activity-entry/api/activityEntryApi";
import { useAuthStore } from "@features/auth/store/authStore";

export type TemplatePreviewContext = {
  columnsSpec: ReportColumnsSpec;
  onPreview: (payload: { name: string; row: Record<string, unknown> }) => void;
};

export interface ReportPageTemplateProps {
  report: UseReportPageResult;
  /** Noun used in copy, e.g. "church" | "district" | "nation". */
  scopeNoun: string;
  /** Renders the leftmost name cell per row (scope-specific review trigger / eyeball / child media). */
  renderRowName: (row: TanRow<ReportMetricRow>, ctx: TemplatePreviewContext) => React.ReactNode;
  /** Optional header-level actions (e.g. nation's aggregate "Request Review" button). */
  headerActions?: React.ReactNode;
  /** Override the status note copy (pass `null` to hide it). `undefined` uses the default. */
  statusNote?: React.ReactNode;
  /** Override the notes-card hint copy. `undefined` uses the default. */
  notesHint?: React.ReactNode;
  /** Override the editable-state placeholder for the notes textarea. */
  notesEditablePlaceholder?: string;
  /** Override the "select filters" empty-state prompt copy. */
  emptyStatePrompt?: React.ReactNode;
  /** Scope-specific modals (request-review modal, preview modal). */
  children?: React.ReactNode;
}

/**
 * Shared shell for the church/district/nation department-report pages: status panel,
 * pagination, tree table, notes + media card with Save/Submit/Validate/Approve, and the
 * approve modal. Per-scope divergences come in via `renderRowName`, `headerActions`, and
 * `children` (the scope's own review modals). State + handlers come from
 * `useReportPage`. Template preview lives in this shell.
 */
export default function ReportPageTemplate({
  report,
  scopeNoun,
  renderRowName,
  headerActions,
  statusNote: statusNoteOverride,
  notesHint,
  notesEditablePlaceholder,
  emptyStatePrompt,
  children,
}: ReportPageTemplateProps) {
  const {
    resp,
    loading,
    error,
    meta,
    submission,
    submissionSummary,
    officeSection,
    sectionViews,
    statusName,
    needsAttentionReason,
    needsAttentionDetails,
    approvalNotesDisplay,
    departmentReportId,
    isMissingReport,
    canLoad,
    showPaging,
    permissionFlags,
    permissionsLoading,
    canReviewActivity,
    selectedTag,
    tagIsOpen,
    tagStatusName,
    tags,
    tagsLoading,
    columnsSpec,
    treeData,
    existingMedia,
    mediaRequirements,
    media,
    onPageChange,
    onOfficePageChange,
    onDistrictOriginPageChange,
    setSectionLens,
    activeSection,
    setActiveSection,
    searchParams,
    resultStart,
    resultEnd,
    deptNotes,
    setDeptNotes,
    savingDraft,
    submittingDept,
    validatingDept,
    deptActionError,
    validationResult,
    deptReportLoading,
    downloadingPdf,
    handleDownloadPdf,
    handleSaveDraft,
    handleSubmitDepartmentReport,
    handleValidateDepartmentReport,
    showApproveModal,
    setShowApproveModal,
    approveNotes,
    setApproveNotes,
    approving,
    approveError,
    handleApprove,
    showReviewModal,
    setShowReviewModal,
    requestingReview,
    reviewError,
    setReviewError,
    handleRequestReview,
    entityId,
    departmentId,
    reportTagId,
    scope,
    load,
    loadDepartmentReport,
  } = report;

  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const requestReviewCopy = getRequestReviewCopy(
    currentUserId,
    submission?.submitted_by
  );
  const requestReviewLabel = requestReviewCopy.actionLabel;
  const requestReviewNoun = requestReviewCopy.noun;
  const [officeReviewTemplate, setOfficeReviewTemplate] =
    React.useState<OfficeReportTemplateRow | null>(null);
  const [officeReviewOpen, setOfficeReviewOpen] = React.useState(false);
  const [officeReviewBusy, setOfficeReviewBusy] = React.useState(false);
  const [officeReviewError, setOfficeReviewError] = React.useState<string | null>(null);
  const [previewRow, setPreviewRow] = React.useState<{
    name: string;
    row: Record<string, unknown>;
    columnsSpec: ReportColumnsSpec;
  } | null>(null);

  const openPreview = React.useCallback(
    (payload: { name: string; row: Record<string, unknown> }, spec: ReportColumnsSpec) => {
      setPreviewRow({ ...payload, columnsSpec: spec });
    },
    []
  );

  const handleOfficeReviewConfirm = async ({ reason }: { reason: string }) => {
    if (!officeReviewTemplate?.report_template_id || reportTagId <= 0) {
      setOfficeReviewError("Report template is required.");
      return;
    }
    setOfficeReviewBusy(true);
    setOfficeReviewError(null);
    try {
      const result =
        scope === "district"
          ? await requestDistrictActivityEntryReviewAsync(
              entityId,
              officeReviewTemplate.report_template_id,
              reportTagId,
              { review_notes: reason }
            )
          : await requestNationActivityEntryReviewAsync(
              entityId,
              officeReviewTemplate.report_template_id,
              reportTagId,
              { review_notes: reason }
            );
      if (!result.isSuccess()) {
        throw new Error(result.getError()?.detail || "Failed to request review.");
      }
      setOfficeReviewOpen(false);
      setOfficeReviewTemplate(null);
      await load();
      await loadDepartmentReport();
    } catch (e) {
      setOfficeReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setOfficeReviewBusy(false);
    }
  };

  const renderOfficeTemplateName = React.useCallback(
    (row: TanRow<ReportMetricRow>, officeColumnsSpec: ReportColumnsSpec) => {
      const original = row.original ?? {};
      const name = String(original.name ?? "");
      const rowMeta = original.__meta as { template_id?: number; is_sub_group?: boolean } | undefined;
      const reportTemplateId =
        Number(
          rowMeta?.template_id ??
            (original as { template_id?: number; report_template_id?: number }).template_id ??
            (original as { report_template_id?: number }).report_template_id
        ) || 0;
      const isTemplateRow = row.depth === 0;
      const isSubGroup =
        original.__rowType === "sub_group" ||
        rowMeta?.is_sub_group === true ||
        row.depth > 0;
      const showEye = isTemplateRow || isSubGroup;
      const templates = officeSection?.type === "office" ? officeSection.templates : [];
      const template =
        templates.find((item) => item.report_template_id === reportTemplateId) ??
        ({
          report_template_id: reportTemplateId,
          report_template_name: name,
        } as OfficeReportTemplateRow);
      const activityStatus = template.activity_report_status_name ?? null;
      return (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            width: "100%",
            minWidth: 0,
          }}
        >
          {showEye ? (
            <TemplateRowPreviewButton
              onPreview={() =>
                openPreview({ name: name || "Template", row: original }, officeColumnsSpec)
              }
            />
          ) : null}
          {isTemplateRow ? (
            <TemplateRowReviewAction
              activityStatus={activityStatus}
              parentDepartmentStatus={statusName}
              canReviewActivity={canReviewActivity}
              permissionsLoading={permissionsLoading}
              busy={officeReviewBusy}
              disabled={reportTemplateId <= 0}
              onRequestReview={() => {
                setOfficeReviewTemplate(template);
                setOfficeReviewError(null);
                setOfficeReviewOpen(true);
              }}
            />
          ) : null}
          <span
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              fontWeight: isTemplateRow ? 600 : 500,
              whiteSpace: "normal",
              overflowWrap: "normal",
              lineHeight: 1.25,
            }}
            title={name}
          >
            {name}
          </span>
        </div>
      );
    },
    [
      canReviewActivity,
      officeReviewBusy,
      officeSection,
      openPreview,
      permissionsLoading,
      statusName,
    ]
  );

  const canEdit = permissionFlags.fieldsEditable;
  const { showSave, showSubmit, showValidate, showManualValidate, showApprove, showReview, showUploadMedia } =
    permissionFlags;
  const canRemoveMedia =
    permissionFlags.fieldsEditable && permissionFlags.canRemoveMedia;

  const notesRef = React.useRef<HTMLTextAreaElement>(null);
  React.useEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [deptNotes]);

  const canApproveNow = canApproveDepartmentReport(statusName);
  const canRequestReviewNow = canRequestDepartmentReportReview(statusName);
  const canDownload = canDownloadReport(statusName);
  const actionsVisible = !permissionsLoading;
  const submitDeptDisabled =
    loading ||
    !canLoad ||
    savingDraft ||
    submittingDept ||
    validatingDept ||
    media.mediaActionId !== null ||
    deptReportLoading ||
    !canEdit ||
    !canSubmitDepartmentReport(statusName);
  const saveDraftDisabled =
    loading ||
    !canLoad ||
    savingDraft ||
    submittingDept ||
    validatingDept ||
    media.mediaActionId !== null ||
    deptReportLoading ||
    !canEdit;
  const validateDeptDisabled =
    loading ||
    !canLoad ||
    savingDraft ||
    submittingDept ||
    validatingDept ||
    deptReportLoading ||
    !canValidateDepartmentReport(statusName);

  const defaultStatusNote = canEdit
    ? `This ${scopeNoun} report is editable. Save Draft to keep working or Submit when it is ready for validation.`
    : statusName === "SUBMITTED"
      ? `${getWorkflowStatusLabel("SUBMITTED")} ${scopeNoun} reports are view-only until validation or review moves them to ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`
      : statusName === "AWAITING_APPROVAL"
        ? `${getWorkflowStatusLabel("AWAITING_APPROVAL")} reports are locked while approval is pending.`
        : statusName === "APPROVED"
          ? `${getWorkflowStatusLabel("APPROVED")} reports are locked and view-only.`
          : "This report is view-only in its current workflow state.";
  const statusNote = statusNoteOverride !== undefined ? statusNoteOverride : defaultStatusNote;

  const basePath = useEntityBasePath();
  const breadcrumbItems = React.useMemo(
    () =>
      basePath
        ? departmentReportsListToViewTrail(basePath, meta?.department_name)
        : [],
    [basePath, meta?.department_name]
  );

  return (
    <Container fluid className="py-4">
      <PageBreadcrumb items={breadcrumbItems} />
      <ReportActionsPanel
        meta={{
          scopeLabel: "",
          scopeName: "",
          departmentName: meta?.department_name ?? "—",
          reportTagName: meta?.report_tag_name ?? "—",
          reportTagStatusName: tagStatusName,
          reportTagDeadline: selectedTag?.deadline ?? null,
          statusName,
          submittedAt: submission?.submitted_at ?? null,
          submittedBy: submission?.submitted_by ?? null,
          submittedByName: submission?.submitted_by_name ?? null,
          approvedAt: submission?.approved_at ?? null,
          approvedByName: submission?.approved_by_name ?? null,
          departmentReportId,
        }}
        statusLabel="Status"
        statusNote={statusNote}
        needsAttentionReason={needsAttentionReason}
        needsAttentionDetails={needsAttentionDetails}
        statusAccessory={
          <SubmissionSummaryControl
            summary={submissionSummary}
            since={selectedTag?.since}
            until={selectedTag?.until}
            deadline={selectedTag?.deadline}
          />
        }
        notices={
          <>
            {shouldShowDeadlineUrgencyBanner({
              tagIsOpen,
              deadline: selectedTag?.deadline,
              isComplete: isDepartmentReportDeadlineComplete(statusName),
            }) ? (
              <DeadlineStatusBanner
                since={selectedTag?.since}
                until={selectedTag?.until}
                deadline={selectedTag?.deadline}
                tagIsOpen={tagIsOpen}
              />
            ) : null}
            {!tagIsOpen ? (
              <Alert variant="warning" className="py-2 mb-2 small">
                This reporting period is not open. Saving and submitting are unavailable.
              </Alert>
            ) : null}
            {validationResult ? (
              <Alert
                variant={validationResult.is_valid ? "success" : "warning"}
                className="py-2 mb-0 small"
              >
                {validationResult.message}
              </Alert>
            ) : null}
          </>
        }
        actions={headerActions}
      />

      {meta && showPaging && resp && (
        <TablePaginationFooter
          className="mb-3"
          testId={`${scopeNoun}-report-pagination`}
          info={`Showing ${resultStart} to ${resultEnd} of ${resp.total} results`}
        >
          <PaginationBar
            page={resp.current_page}
            totalPages={resp.last_page}
            onPage={onPageChange}
          />
        </TablePaginationFooter>
      )}

      {loading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading report...</span>
        </div>
      )}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {canLoad && isMissingReport && !loading ? <NoDepartmentReportNotice /> : null}

      {!canLoad && !loading && !resp && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            {entityId > 0 && departmentId > 0 && reportTagId === 0 && tagsLoading ? (
              <p className="text-muted mb-0 d-flex align-items-center justify-content-center gap-2">
                <Spinner animation="border" size="sm" />
                Loading reporting periods…
              </p>
            ) : entityId > 0 && departmentId > 0 && reportTagId === 0 && tags.length === 0 ? (
              <p className="text-muted mb-0">No reporting periods found for this {scopeNoun}.</p>
            ) : (
              <p className="text-muted mb-0">
                {emptyStatePrompt ?? "Select a department and reporting period to view the aggregated report."}
              </p>
            )}
          </Card.Body>
        </Card>
      )}

      {resp && !loading && (
        <>
          {sectionViews.length > 0
            ? (
                <ReportSectionTables
                  views={sectionViews}
                  activeKey={activeSection}
                  onSectionChange={setActiveSection}
                  renderSection={(view, { showSectionHeader }) => {
                    const sectionPager =
                      view.section.type === "office"
                        ? onOfficePageChange
                        : view.section.type === "district_origin"
                          ? onDistrictOriginPageChange
                          : onPageChange;
                    const range = getPaginationRange({
                      page: view.currentPage,
                      perPage: view.perPage,
                      total: view.total,
                      currentCount: view.tree.length,
                    });
                    return (
                      <Card key={view.section.type} className="mb-3">
                        {showSectionHeader ? (
                          <Card.Header className="fw-semibold">{view.section.label}</Card.Header>
                        ) : null}
                        <Card.Body className="p-0">
                          {(() => {
                            const sectionKey = (
                              view.section.type === "office"
                                ? "office"
                                : view.section.type === "district_origin"
                                  ? "district_origin"
                                  : "children"
                            ) as SectionLensKey;
                            const lens = readSectionLens(searchParams, sectionKey);
                            const viewMeta =
                              "view_meta" in view.section ? view.section.view_meta : null;
                            const timelineEvents =
                              "timeline_events" in view.section
                                ? view.section.timeline_events
                                : null;
                            const showTimeline = lens.view === "timeline";
                            const events = Array.isArray(timelineEvents) ? timelineEvents : [];

                            return (
                              <>
                                {showTimeline ? (
                                  <>
                                    <div className="px-3 py-2 border-bottom">
                                      <ReportSectionViewToolbar
                                        sectionType={view.section.type}
                                        lens={lens}
                                        viewMeta={viewMeta}
                                        onChange={(next) => setSectionLens(sectionKey, next)}
                                      />
                                    </div>
                                    <div className="p-3">
                                      {events.length === 0 ? (
                                        <p className="text-muted mb-0 small">No events in this view.</p>
                                      ) : (
                                        <ul className="list-unstyled mb-0 small">
                                          {events.map((ev, idx) => (
                                            <li
                                              key={`${ev.kind}-${ev.entity_id ?? "x"}-${ev.template_id ?? idx}-${idx}`}
                                              className="border-bottom py-2"
                                            >
                                              <div className="fw-semibold">
                                                {ev.template_name || ev.entity_name || ev.kind}
                                              </div>
                                              <div className="text-muted">
                                                {formatTimelineEventSubtitle(ev)}
                                              </div>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  </>
                                ) : view.tree.length === 0 ? (
                                  <>
                                    <div className="px-3 py-2 border-bottom">
                                      <ReportSectionViewToolbar
                                        sectionType={view.section.type}
                                        lens={lens}
                                        viewMeta={viewMeta}
                                        onChange={(next) => setSectionLens(sectionKey, next)}
                                      />
                                    </div>
                                    <div className="p-3">
                                      <p className="text-muted mb-0 small">
                                        No data available for this view.
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  <ReportTreeTable
                                    columnsSpec={view.columnsSpec}
                                    data={view.tree}
                                    defaultExpanded={false}
                                    totals={view.totals}
                                    totalsLabel="TOTAL"
                                    toolbarStart={
                                      <ReportSectionViewToolbar
                                        sectionType={view.section.type}
                                        lens={lens}
                                        viewMeta={viewMeta}
                                        onChange={(next) => setSectionLens(sectionKey, next)}
                                      />
                                    }
                                    renderName={
                                      view.section.type === "office"
                                        ? (row) =>
                                            renderOfficeTemplateName(row, view.columnsSpec)
                                        : (row) =>
                                            renderRowName(row, {
                                              columnsSpec: view.columnsSpec,
                                              onPreview: (payload) =>
                                                openPreview(payload, view.columnsSpec),
                                            })
                                    }
                                  />
                                )}
                              </>
                            );
                          })()}
                        </Card.Body>
                        {view.lastPage > 1 ? (
                          <TablePaginationFooter
                            className="px-3 py-2"
                            testId={`${scopeNoun}-${view.section.type}-pagination`}
                            info={`Showing ${range.start} to ${range.end} of ${view.total} results`}
                          >
                            <PaginationBar
                              page={view.currentPage}
                              totalPages={view.lastPage}
                              onPage={sectionPager}
                            />
                          </TablePaginationFooter>
                        ) : null}
                      </Card>
                    );
                  }}
                />
              )
            : scope === "church" ||
                ((resp.data?.rows?.length ?? 0) > 0 || treeData.length > 0) ? (
              <Card>
                <Card.Body className="p-0">
                  {(() => {
                    const churchLens = readSectionLens(searchParams, "church");
                    const churchViewMeta =
                      resp?.data?.view_meta ??
                      (meta as { view_meta?: ReportFormSectionViewMeta } | undefined)?.view_meta ??
                      null;
                    const churchTimeline =
                      resp?.data?.timeline_events ??
                      (meta as { timeline_events?: ReportFormTimelineEvent[] } | undefined)
                        ?.timeline_events ??
                      null;
                    const showChurchTimeline = churchLens.view === "timeline";
                    const churchEvents = Array.isArray(churchTimeline) ? churchTimeline : [];

                    return (
                      <>
                        {showChurchTimeline ? (
                          <>
                            <div className="px-3 py-2 border-bottom">
                              <ReportSectionViewToolbar
                                sectionType="church"
                                lens={churchLens}
                                viewMeta={churchViewMeta}
                                onChange={(next) => setSectionLens("church", next)}
                              />
                            </div>
                            <div className="p-3">
                              {churchEvents.length === 0 ? (
                                <p className="text-muted mb-0 small">No events in this view.</p>
                              ) : (
                                <ul className="list-unstyled mb-0 small">
                                  {churchEvents.map((ev, idx) => (
                                    <li
                                      key={`${ev.kind}-${ev.template_id ?? idx}-${idx}`}
                                      className="border-bottom py-2"
                                    >
                                      <div className="fw-semibold">
                                        {ev.template_name || ev.entity_name || ev.kind}
                                      </div>
                                      <div className="text-muted">
                                        {formatTimelineEventSubtitle(ev)}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </>
                        ) : treeData.length === 0 ? (
                          <>
                            <div className="px-3 py-2 border-bottom">
                              <ReportSectionViewToolbar
                                sectionType="church"
                                lens={churchLens}
                                viewMeta={churchViewMeta}
                                onChange={(next) => setSectionLens("church", next)}
                              />
                            </div>
                            <div className="p-3">
                              <p className="text-muted mb-0 small">
                                No data available for this view.
                              </p>
                            </div>
                          </>
                        ) : (
                          <ReportTreeTable
                            columnsSpec={columnsSpec}
                            data={treeData}
                            defaultExpanded={false}
                            totals={resp?.data?.totals ?? null}
                            totalsLabel="TOTAL"
                            toolbarStart={
                              <ReportSectionViewToolbar
                                sectionType="church"
                                lens={churchLens}
                                viewMeta={churchViewMeta}
                                onChange={(next) => setSectionLens("church", next)}
                              />
                            }
                            renderName={(row) =>
                              renderRowName(row, {
                                columnsSpec,
                                onPreview: (payload) => openPreview(payload, columnsSpec),
                              })
                            }
                          />
                        )}
                      </>
                    );
                  })()}
                </Card.Body>
              </Card>
            ) : null}
        </>
      )}

      {canLoad && meta && (
        <Card className="mt-4 flex-grow-1">
          <Card.Body>
            <div className="fw-semibold">Department Report Notes</div>
            <div className="text-muted small mb-3">
              {notesHint !== undefined
                ? notesHint
                : canEdit
                  ? `Notes are editable while the report is in ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`
                  : "Notes are read-only once the report has been submitted for review or approval."}
            </div>
            <Form.Group className="mb-0">
              <Form.Control
                ref={notesRef as React.Ref<HTMLTextAreaElement>}
                as="textarea"
                value={deptNotes}
                onChange={(e) => {
                  setDeptNotes(e.target.value);
                  (e.target as HTMLTextAreaElement).style.height = "auto";
                  (e.target as HTMLTextAreaElement).style.height = `${(e.target as HTMLTextAreaElement).scrollHeight}px`;
                }}
                disabled={!canEdit || savingDraft || submittingDept || validatingDept}
                placeholder={
                  canEdit
                    ? notesEditablePlaceholder ?? "Enter notes for this report..."
                    : `Notes can only be edited when the report is ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`
                }
                style={{ overflow: "hidden", resize: "none", minHeight: "6rem" }}
              />
            </Form.Group>
            <DepartmentReportMediaPanel
              requirements={mediaRequirements}
              existingMedia={existingMedia}
              captionDrafts={media.captionDrafts}
              mediaActionId={media.mediaActionId}
              captionSavingId={media.captionSavingId}
              error={media.error}
              editable={showUploadMedia}
              allowRemove={canRemoveMedia}
              busy={savingDraft || submittingDept || validatingDept}
              uploadBusy={media.uploadBusy}
              uploadDraft={media.uploadDraft}
              uploadError={media.uploadError}
              namedFileInputRefs={media.namedFileInputRefs}
              otherFileInputRef={media.otherFileInputRef}
              onNamedFileChange={media.openNamedUpload}
              onOtherFileChange={media.openOtherUpload}
              onUploadDraftCaptionChange={media.setUploadDraftCaption}
              onConfirmUpload={media.confirmUpload}
              onCancelUpload={media.clearUploadDraft}
              onCaptionDraftChange={media.setCaptionDraft}
              onSaveCaption={media.saveCaption}
              onView={media.viewMedia}
              onRemove={media.handleRemoveMedia}
            />
            {approvalNotesDisplay ? (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Approval Notes</div>
                <Form.Control
                  as="textarea"
                  className="text-muted"
                  value={approvalNotesDisplay}
                  disabled
                  readOnly
                  style={{ overflow: "hidden", resize: "none", minHeight: "4rem" }}
                />
              </div>
            ) : null}
            {deptActionError ? (
              <Alert variant="danger" className="mt-3 mb-0">
                {deptActionError}
              </Alert>
            ) : null}
            <div className="d-flex justify-content-end gap-2 mt-3">
              {actionsVisible && showValidate ? (
                <Button
                  variant="outline-success"
                  className="btn-action"
                  onClick={handleValidateDepartmentReport}
                  disabled={validateDeptDisabled}
                >
                  {validatingDept ? (
                    <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  ) : null}
                  {normalizeReportWorkflowStatus(statusName) === "NEEDS_ATTENTION"
                    ? "Re-Validate"
                    : "Validate"}
                </Button>
              ) : null}
              {actionsVisible && showManualValidate ? (
                <Button
                  variant="outline-secondary"
                  className="btn-action"
                  onClick={handleValidateDepartmentReport}
                  disabled={loading || !canLoad || validatingDept || deptReportLoading}
                  title="Run validation manually for submitted reports"
                >
                  {validatingDept ? (
                    <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                  ) : null}
                  Manual Validate
                </Button>
              ) : null}
              {actionsVisible && showSave ? (
              <Button
                variant="outline-primary"
                className="btn-action"
                onClick={handleSaveDraft}
                disabled={saveDraftDisabled}
                aria-busy={savingDraft}
              >
                {savingDraft ? (
                  <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Saving…</>
                ) : (
                  "Save Draft"
                )}
              </Button>
              ) : null}
              {actionsVisible && showSubmit ? (
                <Button
                  variant="primary"
                  className="btn-action"
                  onClick={handleSubmitDepartmentReport}
                  disabled={submitDeptDisabled}
                  aria-busy={submittingDept}
                >
                  {submittingDept ? (
                    <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Submitting…</>
                  ) : (
                    <><FiSend className="me-2" aria-hidden />{statusName === "NEEDS_ATTENTION" ? "Resubmit" : "Submit"}</>
                  )}
                </Button>
              ) : null}
              {actionsVisible &&
              showReview &&
              canRequestReviewNow &&
              departmentReportId &&
              !deptReportLoading ? (
                <Button
                  variant="outline-primary"
                  className="btn-action"
                  onClick={() => {
                    setReviewError(null);
                    setShowReviewModal(true);
                  }}
                  disabled={
                    loading ||
                    !canLoad ||
                    requestingReview ||
                    approving ||
                    validatingDept
                  }
                  title="Return this report for corrections"
                >
                  <FiSend className="me-2" aria-hidden />
                  {requestReviewLabel}
                </Button>
              ) : null}
              {actionsVisible && showApprove && canApproveNow && departmentReportId && !deptReportLoading ? (
                <Button
                  variant="success"
                  className="btn-action"
                  onClick={() => {
                    setApproveNotes("");
                    setShowApproveModal(true);
                  }}
                  disabled={loading || !canLoad || approving || validatingDept}
                  title="Approve this report"
                >
                  <FiCheckCircle className="me-2" aria-hidden />
                  Approve Report
                </Button>
              ) : null}
              {canDownload ? (
                <Button
                  variant="primary"
                  className="btn-action"
                  onClick={handleDownloadPdf}
                  disabled={loading || !canLoad || downloadingPdf}
                  aria-busy={downloadingPdf}
                  title="Download this report as a PDF"
                >
                  {downloadingPdf ? (
                    <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Generating…</>
                  ) : (
                    <><FiDownload className="me-2" aria-hidden />Download PDF</>
                  )}
                </Button>
              ) : null}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Approve modal (shared) */}
      <Modal
        show={showApproveModal}
        onHide={() => {
          if (approving) return;
          setShowApproveModal(false);
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!approving}>
          <Modal.Title>Approve Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-2">
            Approving the aggregated {scopeNoun} report will move the status to <strong>{getWorkflowStatusLabel("APPROVED")}</strong>.
          </div>
          <Form.Group>
            <Form.Label className="fw-medium">Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              disabled={approving}
              placeholder="Add approval notes (optional)..."
            />
          </Form.Group>
          {approveError ? (
            <Alert variant="danger" className="mt-3 mb-0">
              {approveError}
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowApproveModal(false)} disabled={approving}>
            <FiX className="me-2" />
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleApprove}
            disabled={approving || !departmentReportId || !canApproveNow}
          >
            {approving ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <FiCheckCircle className="me-2" />
            )}
            Confirm Approve
          </Button>
        </Modal.Footer>
      </Modal>

      <RequestReviewModal
        show={showReviewModal}
        onHide={() => {
          if (!requestingReview) setShowReviewModal(false);
        }}
        title={requestReviewLabel}
        submittedBy={submission?.submitted_by}
        subject={
          <>
            You are requesting {requestReviewNoun} for this {scopeNoun} department report. It will be marked as{" "}
            <span className="fw-semibold">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</span> so it can be edited and resubmitted.
          </>
        }
        busy={requestingReview}
        error={reviewError}
        onConfirm={handleRequestReview}
      />

      <RequestReviewModal
        show={officeReviewOpen}
        onHide={() => {
          if (!officeReviewBusy) {
            setOfficeReviewOpen(false);
            setOfficeReviewTemplate(null);
          }
        }}
        title={getRequestReviewCopy(
          currentUserId,
          officeReviewTemplate?.submitted_by
        ).actionLabel}
        submittedBy={officeReviewTemplate?.submitted_by}
        subject={
          <>
            You are requesting{" "}
            {getRequestReviewCopy(
              currentUserId,
              officeReviewTemplate?.submitted_by
            ).noun}{" "}
            for office activity template{" "}
            <span className="fw-semibold">
              {officeReviewTemplate?.report_template_name ?? "this template"}
            </span>
            . It will be marked as{" "}
            <span className="fw-semibold">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</span> so it can be corrected and resubmitted.
          </>
        }
        busy={officeReviewBusy}
        error={officeReviewError}
        onConfirm={handleOfficeReviewConfirm}
      />

      <ReportPreviewModal
        show={!!previewRow}
        onHide={() => setPreviewRow(null)}
        title={previewRow?.name ?? "Template"}
        subtitle={meta?.report_tag_name}
      >
        {previewRow ? (
          <GenericSubmissionPreview columnsSpec={previewRow.columnsSpec} row={previewRow.row} />
        ) : null}
      </ReportPreviewModal>

      {children}
    </Container>
  );
}
